import { persistReducer, createMigrate, purgeStoredState } from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';
import migrations from './migrations';
import { combineReducers } from '@reduxjs/toolkit';

import authReducer from 'redux/slices/auth';
import javelinStoreReducer from 'redux/slices/javelinStore';
import fundraiserReducer from 'redux/slices/fundraiser';
import productReducer from 'redux/slices/product';
import orderReducer from 'redux/slices/order';
import notificationReducer from 'redux/slices/notification';
import cartReducer from 'redux/slices/cart';
import searchReducer from 'redux/slices/search';
import checkoutFormReducer from 'redux/slices/checkoutForm';
import companyReducer from 'redux/slices/company';
import checkoutReducer from 'redux/slices/checkout';
import sidebarReducer from 'redux/slices/sidebar';
import transactionReducer from 'redux/slices/transaction';
import payoutReducer from 'redux/slices/payout';
import refundReducer from 'redux/slices/refund';
import disputeReducer from 'redux/slices/dispute';
import webhookReducer from 'redux/slices/webhook';
import transferReducer from 'redux/slices/transfer';
import clientReducer from 'redux/slices/client';
import integrationReducer from 'redux/slices/integration';

import {
  LOGOUT_ACTION,
  LOGIN_ACTION,
  FORGOT_PASSWORD_ACTION,
  RESET_PASSWORD_ACTION,
} from 'redux/slices/auth/types';
import { isDevelopment, isTest } from 'utils/environment';
import { brands, HAILOPAY } from 'utils/brand-context';
import PATHS from 'routes/paths';
import {
  authConfig,
  cartConfig,
  checkoutConfig,
  clientConfig,
  companyConfig,
  disputeConfig,
  integrationConfig,
  payoutConfig,
  refundConfig,
  transactionConfig,
  transferConfig,
  webhookConfig,
} from './configs';

const rootPersistConfig = {
  key: 'root',
  version: 8,
  storage: storageSession,
  blacklist: [
    'auth',
    'cart',
    'checkout',
    'client',
    'company',
    'dispute',
    'fundraiser',
    'integration',
    'javelinStore',
    'notification',
    'order',
    'payout',
    'product',
    'refund',
    'search',
    'transaction',
    'transfer',
    'webhook',
  ],
  migrate: createMigrate(migrations.root, { debug: isDevelopment }),
};

const appReducer = combineReducers({
  auth: persistReducer(authConfig, authReducer),
  cart: persistReducer(cartConfig, cartReducer),
  checkout: persistReducer(checkoutConfig, checkoutReducer),
  checkoutForm: checkoutFormReducer,
  client: persistReducer(clientConfig, clientReducer),
  company: persistReducer(companyConfig, companyReducer),
  dispute: persistReducer(disputeConfig, disputeReducer),
  fundraiser: fundraiserReducer,
  integration: persistReducer(integrationConfig, integrationReducer),
  javelinStore: javelinStoreReducer,
  notification: notificationReducer,
  order: orderReducer,
  payout: persistReducer(payoutConfig, payoutReducer),
  product: productReducer,
  refund: persistReducer(refundConfig, refundReducer),
  search: searchReducer,
  sidebar: sidebarReducer,
  transaction: persistReducer(transactionConfig, transactionReducer),
  transfer: persistReducer(transferConfig, transferReducer),
  webhook: persistReducer(webhookConfig, webhookReducer),
});

const rootReducer = (state, action) => {
  if (
    !isTest &&
    [PATHS.resetPassword, PATHS.forgotPassword, PATHS.login].indexOf(
      window.location.pathname
    ) === -1 &&
    (action.type === `${LOGOUT_ACTION}/fulfilled` ||
      (action?.payload?.status === 401 &&
        [
          `${LOGIN_ACTION}/rejected`,
          `${FORGOT_PASSWORD_ACTION}/rejected`,
          `${RESET_PASSWORD_ACTION}/rejected`,
        ].indexOf(action.type) === -1))
  ) {
    purgeStoredState(rootPersistConfig);
    const [subdomain] = window.location.hostname.split('.');
    const brand = brands[subdomain] || brands[HAILOPAY];
    window.location.replace(brand.loginUrl);
  }

  return appReducer(state, action);
};

export default persistReducer(rootPersistConfig, rootReducer);
